import { SimboloDoPortal } from '@/components/marca/SimboloDoPortal';
import { useEffect, useState, type ComponentType } from "react";
import { Link } from "react-router-dom";
import {
  ArrowPathIcon, CameraIcon, ChartBarIcon, CheckCircleIcon, ComputerDesktopIcon, DevicePhoneMobileIcon, FingerPrintIcon,
  GlobeAltIcon, LockClosedIcon, PrinterIcon, ServerStackIcon, ShieldCheckIcon, UserGroupIcon, WifiIcon,
} from "@heroicons/react/24/outline";
import { api } from "@/utils/api";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { urlDeDownload } from "@/features/aplicativo/downloadDoAplicativo";

/**
 * A PÁGINA INICIAL (24/10/2026, refeita no padrão das telas — docs/PADRAO-DE-TELAS.md). Pública: o que o portal faz, onde
 * usar, números reais e agregados, as telas de verdade (capturadas com as contas de DEMONSTRAÇÃO — escola fictícia, alunos
 * sintéticos; `backend/src/scripts/capturasDaPaginaInicial.ts`), privacidade e perguntas frequentes.
 *
 * QUEM PODE, ENUMERADO (achados 69, 70, 74, 83): o botão de quem já entrou é um mapa papel → destino, e papel que não está
 * no mapa fica SEM botão — nunca um `else` que herda a porta de outro papel.
 *
 * LEVE: as capturas são WebP em dois tamanhos (`public/inicio/`), com largura e altura declaradas (sem salto de layout),
 * e as de baixo carregam só quando chegam perto da tela. A composição do topo só existe a partir de 1024 px — no celular
 * o `<picture>` entrega um pixel vazio, e nada é baixado.
 *
 * As faixas vão de ponta a ponta (o `MainLayout` não põe o contêiner nesta rota) e o conteúdo fica em `max-w-6xl`.
 */
const CONTINUAR: Record<string, { rotulo: string; para: string }> = {
  TEACHER: { rotulo: "Continuar para o Painel do professor", para: "/teacher" },
  SCHOOL_STAFF: { rotulo: "Continuar para o Painel da escola", para: "/gestao" },
  STUDENT: { rotulo: "Continuar para Meus simulados", para: "/student" },
  ADMIN: { rotulo: "Continuar para o Painel da administração", para: "/admin" },
};

type Numeros = { entregas: number; simuladosAplicados: number; questoesNoAcervo: number; questoesComHabilidadeConfirmada?: number; atualizadoEm: string };
type Lojas = { appStore: string; googlePlay: string | null };
const PIXEL = "data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==";
const mil = (n: number) => n.toLocaleString("pt-BR");

const PRINCIPAL = "inline-flex min-h-[48px] items-center justify-center rounded-lg bg-marca-600 px-6 text-base font-semibold text-white shadow-sm transition hover:bg-marca-700";
const SECUNDARIO = "inline-flex min-h-[48px] items-center justify-center rounded-lg border border-gray-300 bg-white px-6 text-base font-semibold text-marca transition hover:bg-marca-50";

const FORMAS: Array<{ Icone: ComponentType<{ className?: string }>; titulo: string; texto: string }> = [
  { Icone: GlobeAltIcon, titulo: "Online", texto: "O aluno responde no celular ou no computador, de casa ou do laboratório. As objetivas são corrigidas na hora." },
  { Icone: PrinterIcon, titulo: "Impressa", texto: "Caderno e folha de respostas prontos para imprimir. O professor lê as folhas com a câmera do celular, e o portal corrige." },
  { Icone: WifiIcon, titulo: "Sem internet", texto: "O aplicativo para computador abre a sala na rede da escola; os alunos entram pelo celular. Os resultados sobem quando a internet volta." },
];

const PASSOS = [
  { titulo: "Elaborar", texto: "Questões do acervo compartilhado, classificadas por SAEB, BNCC e Enem e com a dificuldade medida nas turmas — ou crie as suas, com fórmulas matemáticas." },
  { titulo: "Montar", texto: "Escolha à mão ou sorteie por descritor e dificuldade; defina as turmas." },
  { titulo: "Aplicar", texto: "Online, impressa ou sem internet — a mesma prova, pelo caminho que a escola tiver." },
  { titulo: "Corrigir", texto: "Objetivas sozinhas; discursivas pelo professor, com a foto da resolução do aluno." },
  { titulo: "Analisar", texto: "Acerto por descritor, mapa da turma, níveis de desempenho e o que retomar." },
];

const NOVIDADES: Array<{ Icone: ComponentType<{ className?: string }>; texto: string }> = [
  { Icone: ShieldCheckIcon, texto: "Modo prova no celular" },
  { Icone: FingerPrintIcon, texto: "Entrar com a digital ou o Face ID" },
  { Icone: CameraIcon, texto: "Foto da resolução nas discursivas" },
  { Icone: UserGroupIcon, texto: "Trocar de conta sem sair" },
  { Icone: ArrowPathIcon, texto: "Telas novas sem passar pela loja" },
];

const GALERIA = [
  { nome: "descritor", titulo: "Relatório por descritor", texto: "O acerto de cada descritor do SAEB, turma a turma." },
  { nome: "mapa", titulo: "Mapa de descritores", texto: "Os pontos frágeis de cada turma, em cores." },
  { nome: "niveis", titulo: "Níveis de desempenho", texto: "Quantos alunos em cada nível, para planejar a retomada." },
  { nome: "painel", titulo: "Painel do professor", texto: "O que fazer hoje: provas abertas, correções pendentes e entregas." },
];

const CELULARES: Array<[string, string]> = [
  ["celular-correcao", "A leitura das folhas de respostas pela câmera do celular."],
  ["celular-simulados", "Os simulados do professor no aplicativo."],
  ["celular-aluno", "A lista de provas do aluno no aplicativo."],
];

const PERGUNTAS = [
  { p: "Quem pode usar?", r: "Professores e equipes gestoras da rede estadual de Alagoas entram com o e-mail institucional. Quem é de outra rede pede o acesso, e o pedido é avaliado. Os alunos recebem usuário e senha da escola." },
  { p: "Quanto custa?", r: "Nada. O portal é gratuito para professores e escolas." },
  { p: "Preciso de uma escola cadastrada para começar?", r: "Não. O professor cria as próprias turmas, monta, aplica e corrige sozinho. Se a escola entrar depois, o que ele já fez continua dele." },
  { p: "Funciona sem internet?", r: "Sim. O aplicativo para Windows e Mac abre a sala na rede da escola, e os alunos respondem pelo celular. Os resultados sobem para o portal quando a internet volta." },
  { p: "Como funciona a prova impressa?", r: "O portal gera o caderno e a folha de respostas. O professor lê as folhas com a câmera do celular, uma a uma, ou envia um PDF com o lote escaneado, e a correção sai sozinha." },
  // A resposta diz o que o código faz (relatórios genéricos, 25/10/2026): o padrão é `agrupadorPadrao`
  // (services/reportService.ts) e o "Ver por" é `components/desempenho/VerPor.tsx`. Mudou o agrupamento? Mude esta frase.
  { p: "Os relatórios seguem o SAEB?", r: "Sim, por padrão: quando o SAEB cobre a maior parte das questões, os relatórios mostram o acerto por descritor do SAEB. Em todos os relatórios de habilidade dá para escolher “Ver por” BNCC, habilidade do Enem ou conteúdo — e o simulado sem matriz nenhuma aparece por conteúdo. Nenhuma questão fica de fora." },
  { p: "O aluno precisa ter celular?", r: "Não. A mesma prova pode ser impressa; o celular é do professor, para ler as folhas de respostas." },
];

/**
 * OS DADOS ESTRUTURADOS DA PÁGINA INICIAL (25/10/2026, encontrabilidade): o aplicativo (SoftwareApplication) e as perguntas
 * frequentes (FAQPage, as MESMAS de `PERGUNTAS`, acima). Vão no HTML pronto (`scripts/prerender-publicas.mjs`).
 * `offers` com preço 0 (decisão do pesquisador, 25/10/2026: gratuito para professores e escolas) — e a página DIZ isso, na
 * pergunta "Quanto custa?": o dado estruturado não afirma o que a página não afirma.
 */
const DADOS_ESTRUTURADOS = [
  {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Portal Simulados',
    url: 'https://www.portalsimulados.com.br/',
    description: 'Simulados com diagnóstico por habilidade — SAEB, BNCC e Enem — online, impresso ou sem internet. Para o professor e para a escola.',
    applicationCategory: 'EducationalApplication',
    operatingSystem: 'Web, Android, iOS, iPadOS, Windows, macOS',
    inLanguage: 'pt-BR',
    image: 'https://www.portalsimulados.com.br/og-imagem.png',
    // O contato vai no `provider` (Organization): `email` não é campo de SoftwareApplication (aviso do validador do
    // schema.org, 25/10/2026).
    provider: { '@type': 'Organization', name: 'Portal Simulados', url: 'https://www.portalsimulados.com.br/', email: 'contato@portalsimulados.com.br' },
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'BRL' },
    sameAs: ['https://apps.apple.com/app/portal-simulados/id6761577308'],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: PERGUNTAS.map((q) => ({ '@type': 'Question', name: q.p, acceptedAnswer: { '@type': 'Answer', text: q.r } })),
  },
];

function Captura({ nome, alt, largura, altura, celular = false }: { nome: string; alt: string; largura: number; altura: number; celular?: boolean }) {
  const [pequena, grande] = celular ? [`/inicio/${nome}-390.webp 390w`, `/inicio/${nome}-780.webp 780w`] : [`/inicio/${nome}-640.webp 640w`, `/inicio/${nome}-1280.webp 1280w`];
  return (
    <img
      src={celular ? `/inicio/${nome}-390.webp` : `/inicio/${nome}-640.webp`}
      srcSet={`${pequena}, ${grande}`}
      sizes={celular ? "(min-width: 768px) 220px, 45vw" : "(min-width: 1152px) 560px, (min-width: 768px) 50vw, 100vw"}
      width={largura} height={altura} alt={alt} loading="lazy" decoding="async"
      className="block h-auto w-full"
    />
  );
}

const LandingPage = () => {
  const { isAuthenticated, user } = useAuth();
  // Os domínios vêm de `registration-config`, a mesma fonte do `/cadastro` (nunca escritos aqui). Falha em silêncio.
  const [dominios, setDominios] = useState<string[]>([]);
  const [numeros, setNumeros] = useState<Numeros | null>(null);
  const [lojas, setLojas] = useState<Lojas>({ appStore: "https://apps.apple.com/app/portal-simulados/id6761577308", googlePlay: null });
  useEffect(() => {
    let cancelado = false;
    api.get("/api/auth/registration-config").then((r) => { if (!cancelado) setDominios(r.data?.allowedDomains ?? []); }).catch(() => {});
    api.get("/api/publico/pagina-inicial").then((r) => {
      if (cancelado) return;
      if (r.data?.numeros) setNumeros(r.data.numeros);
      if (r.data?.lojas) setLojas(r.data.lojas);
    }).catch(() => {});
    return () => { cancelado = true; };
  }, []);
  const continuar = isAuthenticated ? CONTINUAR[user?.role ?? ""] : undefined;

  return (
    <div className="bg-white" data-testid="pagina-inicial">
      {DADOS_ESTRUTURADOS.map((d, i) => (
        <script key={i} type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(d).replace(/</g, '\\u003c') }} />
      ))}
      {/* a. O TOPO */}
      <section className="border-b border-gray-100 bg-gradient-to-b from-marca-50 to-white">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-4 py-12 md:py-16 lg:grid-cols-[1.05fr_1fr] lg:py-20">
          <div className="min-w-0">
            <p className="mb-3 inline-flex items-center gap-2 font-titulo text-base font-bold text-marca"><SimboloDoPortal className="h-7 w-7 flex-shrink-0" />Portal Simulados</p>
            <h1 className="font-titulo text-balance text-3xl font-bold leading-tight text-tinta md:text-4xl xl:text-5xl" data-testid="titulo-da-tela">
              Simulados com diagnóstico por habilidade — SAEB, BNCC e Enem — online, impresso ou sem internet
            </h1>
            <p className="mt-4 max-w-xl text-lg text-gray-600">
              Para o professor e para a escola: monte a prova uma vez, aplique como der e veja, turma a turma, o que já foi
              aprendido e o que precisa ser retomado.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              {!isAuthenticated ? (
                <>
                  <Link to="/login" className={PRINCIPAL} data-testid="entrar">Entrar</Link>
                  {/* UM botão de cadastro: o tipo de conta sai do endereço (achado 78). */}
                  <Link to="/cadastro" className={SECUNDARIO}>Criar conta com e-mail da rede</Link>
                  <a href="#como-funciona" className="inline-flex min-h-[48px] items-center justify-center px-2 font-semibold text-marca-700 hover:underline">Como funciona</a>
                </>
              ) : continuar ? (
                <Link to={continuar.para} className={PRINCIPAL} data-testid="continuar">{continuar.rotulo}</Link>
              ) : null}
            </div>
            {!isAuthenticated && (
              <p className="mt-5 max-w-xl text-sm text-gray-600">
                Professores e equipes gestoras da rede estadual de Alagoas entram com o endereço institucional
                {dominios.length > 0 && <> ({dominios.map((d) => `@${d}`).join(", ")})</>}. É de outra rede?{" "}
                <Link to="/solicitar-acesso" className="font-medium text-marca-700 underline">Solicite o acesso</Link>.
              </p>
            )}
          </div>
          {/* A composição: o portal no computador e o app no celular — só a partir de 1024 px. */}
          <div className="relative hidden pb-10 pl-8 lg:block" aria-hidden="true" data-testid="composicao-do-topo">
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl">
              <div className="flex h-7 items-center gap-1.5 border-b border-gray-100 bg-gray-50 px-3">
                <span className="h-2.5 w-2.5 rounded-full bg-gray-300" /><span className="h-2.5 w-2.5 rounded-full bg-gray-300" /><span className="h-2.5 w-2.5 rounded-full bg-gray-300" />
              </div>
              <picture>
                <source media="(min-width: 1024px)" srcSet="/inicio/descritor-640.webp 640w, /inicio/descritor-1280.webp 1280w" sizes="560px" />
                <img src={PIXEL} width={1280} height={800} alt="" fetchPriority="high" className="block h-auto w-full" />
              </picture>
            </div>
            <div className="absolute bottom-0 left-0 w-[30%] overflow-hidden rounded-[1.4rem] border-[6px] border-gray-900 bg-gray-900 shadow-2xl">
              <picture>
                <source media="(min-width: 1024px)" srcSet="/inicio/celular-simulados-390.webp 390w, /inicio/celular-simulados-780.webp 780w" sizes="180px" />
                <img src={PIXEL} width={390} height={844} alt="" className="block h-auto w-full rounded-[1rem]" />
              </picture>
            </div>
          </div>
        </div>
      </section>

      {/* b. ONDE USAR */}
      <section className="mx-auto max-w-6xl px-4 py-10" aria-labelledby="onde-usar">
        <h2 id="onde-usar" className="text-center text-xl font-semibold text-tinta">No navegador, no celular e no tablet (iPhone, iPad e Android) e no computador</h2>
        <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
          <a href={lojas.appStore} className="inline-flex min-h-[48px] items-center gap-2 rounded-lg bg-gray-900 px-5 text-sm font-semibold text-white hover:bg-gray-800" data-testid="selo-app-store" rel="noopener">
            <DevicePhoneMobileIcon className="h-5 w-5" aria-hidden /> Baixar na App Store · iPhone e iPad
          </a>
          {lojas.googlePlay && (
            <a href={lojas.googlePlay} className="inline-flex min-h-[48px] items-center gap-2 rounded-lg bg-gray-900 px-5 text-sm font-semibold text-white hover:bg-gray-800" data-testid="selo-google-play" rel="noopener">
              <DevicePhoneMobileIcon className="h-5 w-5" aria-hidden /> Disponível no Google Play
            </a>
          )}
          {/* Enquanto a chave LOJA_PLAY_PUBLICADA está desligada, o Android é anunciado como "em breve" (25/10/2026). */}
          {!lojas.googlePlay && (
            <span className="inline-flex min-h-[48px] items-center gap-2 rounded-lg border border-dashed border-gray-300 px-5 text-sm font-medium text-gray-600" data-testid="android-em-breve">
              <DevicePhoneMobileIcon className="h-5 w-5" aria-hidden /> Android: em breve na Google Play
            </span>
          )}
          <Link to="/como-usar#aplicativo-para-computador" className="inline-flex min-h-[48px] items-center gap-2 rounded-lg border border-gray-300 bg-white px-5 text-sm font-semibold text-marca hover:bg-marca-50" data-testid="selo-computador">
            <ComputerDesktopIcon className="h-5 w-5" aria-hidden /> Aplicativo para Windows e Mac
          </Link>
        </div>
        <p className="mt-3 text-center text-sm text-gray-600">
          Baixar direto: <a className="inline-flex min-h-[44px] items-center font-medium text-marca-700 underline" href={urlDeDownload("windows")}>Windows</a> ·{" "}
          <a className="inline-flex min-h-[44px] items-center font-medium text-marca-700 underline" href={urlDeDownload("mac")}>Mac (chip Apple)</a> ·{" "}
          <a className="inline-flex min-h-[44px] items-center font-medium text-marca-700 underline" href={urlDeDownload("mac-intel")}>Mac (Intel)</a>
        </p>
      </section>

      {/* c. OS NÚMEROS — agregados, reais, sem nada que identifique pessoa. A altura é reservada (sem salto ao chegar). */}
      <section className="border-y border-gray-100 bg-gray-50" aria-label="O portal em números">
        <dl className="mx-auto grid max-w-6xl grid-cols-3 gap-2 px-4 py-8 text-center" data-testid="numeros-de-confianca">
          {([["simuladosAplicados", "simulados aplicados"], ["entregas", "provas entregues"], ["questoesNoAcervo", "questões no acervo"]] as const).map(([k, rotulo]) => (
            <div key={k} className="flex min-w-0 flex-col-reverse">
              <dt className="mt-1 text-xs text-gray-600 md:text-sm">{rotulo}</dt>
              <dd className="font-titulo text-2xl font-bold text-tinta md:text-4xl">{numeros ? mil(numeros[k]) : <span className="inline-block h-8 w-16 animate-pulse rounded bg-gray-200 md:h-10" />}</dd>
            </div>
          ))}
        </dl>
      </section>

      {/* d. AS TRÊS FORMAS — grade de 3 no computador, lista compacta com ícone no celular. */}
      <section id="recursos" className="mx-auto max-w-6xl scroll-mt-4 px-4 py-14">
        <h2 className="font-titulo text-2xl font-bold text-tinta md:text-center md:text-3xl">Três formas de aplicar</h2>
        <p className="mt-2 max-w-2xl text-gray-600 md:mx-auto md:text-center">O mesmo instrumento, montado uma vez, chega ao aluno pelo caminho que a escola tiver.</p>
        <ul className="mt-8 grid gap-5 md:grid-cols-3 md:gap-6">
          {FORMAS.map(({ Icone, titulo, texto }) => (
            <li key={titulo} className="flex gap-4 md:flex-col md:rounded-xl md:border md:border-gray-200 md:p-6 md:shadow-sm">
              <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg bg-marca-100 text-marca-700"><Icone className="h-6 w-6" /></span>
              <div className="min-w-0">
                <h3 className="font-semibold text-tinta">{titulo}</h3>
                <p className="mt-1 text-sm text-gray-600">{texto}</p>
              </div>
            </li>
          ))}
        </ul>
      </section>

      {/* Para o professor e Para a escola, lado a lado — nenhum dos dois é o caso principal. */}
      <section className="bg-gray-50">
        <div className="mx-auto grid max-w-6xl gap-6 px-4 py-14 md:grid-cols-2">
          <div className="rounded-xl bg-white p-6 shadow-sm">
            <h2 className="text-xl font-bold text-tinta">Para o professor</h2>
            <p className="mt-2 text-gray-600">Você não precisa de escola nenhuma para começar: crie suas turmas, monte, aplique e corrija. Se a sua escola entrar depois, <strong className="text-gray-900">o que você já fez continua seu</strong>.</p>
          </div>
          <div className="rounded-xl bg-white p-6 shadow-sm">
            <h2 className="text-xl font-bold text-tinta">Para a escola</h2>
            <p className="mt-2 text-gray-600">A equipe gestora cadastra as turmas a partir do arquivo do sistema da escola, convida os professores e acompanha todas as turmas. Cada aluno tem <strong className="text-gray-900">uma conta</strong>, pela matrícula.</p>
          </div>
        </div>
      </section>

      {/* COMO FUNCIONA — linha do tempo horizontal no computador, lista no celular. */}
      <section id="como-funciona" className="mx-auto max-w-6xl scroll-mt-4 px-4 py-14">
        <h2 className="font-titulo text-2xl font-bold text-tinta md:text-center md:text-3xl">Como funciona</h2>
        <ol className="relative mt-8 grid gap-6 md:grid-cols-5 md:gap-4">
          <span className="absolute left-[10%] right-[10%] top-5 hidden h-0.5 bg-marca-100 md:block" aria-hidden />
          {PASSOS.map((p, i) => (
            <li key={p.titulo} className="relative flex gap-4 md:flex-col md:items-center md:text-center">
              <span className="relative z-10 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-marca-600 font-bold text-white">{i + 1}</span>
              <div className="min-w-0">
                <h3 className="font-semibold text-tinta">{p.titulo}</h3>
                <p className="mt-1 text-sm text-gray-600">{p.texto}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* e. NOVIDADES DA 1.4 — uma faixa curta. */}
      <section className="bg-marca-700 text-white" aria-labelledby="novidades">
        <div className="mx-auto max-w-6xl px-4 py-8">
          <h2 id="novidades" className="text-sm font-semibold uppercase tracking-wide text-marca-100">Novo no aplicativo 1.4</h2>
          <ul className="mt-3 flex flex-wrap gap-2">
            {NOVIDADES.map(({ Icone, texto }) => (
              <li key={texto} className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-sm"><Icone className="h-4 w-4" aria-hidden />{texto}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* f. O QUE O PROFESSOR VÊ — telas reais, com a conta de demonstração. */}
      <section className="mx-auto max-w-6xl px-4 py-14" aria-labelledby="o-que-ve">
        <h2 id="o-que-ve" className="font-titulo text-2xl font-bold text-tinta md:text-center md:text-3xl">O que o professor vê</h2>
        <p className="mt-2 text-gray-600 md:text-center">Telas do portal com a escola de demonstração — os nomes são fictícios.</p>
        <div className="mt-8 grid gap-6 md:grid-cols-2" data-testid="galeria">
          {GALERIA.map((g) => (
            <figure key={g.nome} className="min-w-0">
              <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
                <Captura nome={g.nome} alt={`${g.titulo} no portal`} largura={1280} altura={800} />
              </div>
              <figcaption className="mt-2"><span className="font-semibold text-gray-900">{g.titulo}.</span> <span className="text-gray-600">{g.texto}</span></figcaption>
            </figure>
          ))}
        </div>
        <div className="mt-10 grid grid-cols-2 gap-6 sm:grid-cols-3 md:mx-auto md:max-w-3xl">
          {CELULARES.map(([nome, texto], i) => (
            <figure key={nome} className={`min-w-0 ${i === 2 ? "hidden sm:block" : ""}`}>
              <div className="overflow-hidden rounded-[1.2rem] border-[5px] border-gray-900 bg-gray-900 shadow-md">
                <Captura nome={nome} alt={texto} largura={390} altura={844} celular />
              </div>
              <figcaption className="mt-2 text-sm text-gray-600">{texto}</figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* g. PRIVACIDADE E SEGURANÇA — só o que é verdade hoje: o banco em São Paulo; a cópia cifrada fora, nos EUA. */}
      <section className="bg-gray-50">
        <div className="mx-auto max-w-6xl px-4 py-14">
          <h2 className="font-titulo text-2xl font-bold text-tinta md:text-center md:text-3xl">Privacidade e segurança</h2>
          <ul className="mt-8 grid gap-4 md:grid-cols-3">
            <li className="rounded-xl bg-white p-5 shadow-sm"><ServerStackIcon className="h-6 w-6 text-marca-700" aria-hidden /><h3 className="mt-2 font-semibold text-tinta">Banco de dados no Brasil</h3><p className="mt-1 text-sm text-gray-600">Os dados do portal ficam num banco em São Paulo.</p></li>
            <li className="rounded-xl bg-white p-5 shadow-sm"><LockClosedIcon className="h-6 w-6 text-marca-700" aria-hidden /><h3 className="mt-2 font-semibold text-tinta">Cópias de segurança cifradas</h3><p className="mt-1 text-sm text-gray-600">Todo dia sai uma cópia cifrada, que só o responsável pelo portal consegue abrir, guardada fora do servidor — num serviço de armazenamento nos Estados Unidos.</p></li>
            <li className="rounded-xl bg-white p-5 shadow-sm"><CheckCircleIcon className="h-6 w-6 text-marca-700" aria-hidden /><h3 className="mt-2 font-semibold text-tinta">LGPD</h3><p className="mt-1 text-sm text-gray-600">Só o necessário para a avaliação, sem venda de dados nem anúncio. A conta pode ser excluída a qualquer momento.</p></li>
          </ul>
          <p className="mt-6 text-center"><Link to="/privacidade" className="inline-flex min-h-[44px] items-center font-medium text-marca-700 underline">Leia a política de privacidade</Link></p>
        </div>
      </section>

      {/* h. PERGUNTAS FREQUENTES */}
      <section className="mx-auto max-w-3xl px-4 py-14" aria-labelledby="perguntas">
        <h2 id="perguntas" className="font-titulo text-2xl font-bold text-tinta md:text-center md:text-3xl">Perguntas frequentes</h2>
        <div className="mt-6 divide-y divide-gray-200 rounded-xl border border-gray-200">
          {PERGUNTAS.map((q) => (
            <details key={q.p} className="group px-4">
              <summary className="flex min-h-[52px] cursor-pointer list-none items-center justify-between gap-3 font-medium text-gray-900">
                {q.p}<span className="text-xl text-gray-500 transition group-open:rotate-45" aria-hidden>+</span>
              </summary>
              <p className="pb-4 text-gray-600">{q.r}</p>
            </details>
          ))}
        </div>
      </section>

      {/* O fechamento: o mesmo convite do topo. */}
      <section className="border-t border-gray-100 bg-marca-50">
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-4 px-4 py-12 text-center">
          <ChartBarIcon className="h-8 w-8 text-marca-700" aria-hidden />
          <p className="text-xl font-semibold text-gray-900">Comece pela próxima avaliação da sua turma.</p>
          {!isAuthenticated ? (
            <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
              <Link to="/login" className={PRINCIPAL}>Entrar</Link>
              <Link to="/cadastro" className={SECUNDARIO}>Criar conta com e-mail da rede</Link>
            </div>
          ) : continuar ? <Link to={continuar.para} className={PRINCIPAL}>{continuar.rotulo}</Link> : null}
        </div>
      </section>

      {/* O rodapé completo da página inicial: contato, privacidade, como usar e lojas. */}
      <section className="border-t border-gray-200 bg-white" aria-label="Mais informações">
        <div className="mx-auto grid max-w-6xl gap-8 px-4 py-10 text-sm sm:grid-cols-2 md:grid-cols-4">
          <div><h2 className="font-semibold text-tinta">Portal Simulados</h2><p className="mt-2 text-gray-600">Avaliação diagnóstica por descritor, para o professor e para a escola.</p></div>
          <div><h2 className="font-semibold text-tinta">Ajuda</h2><ul className="mt-1"><li><Link className="inline-flex min-h-[44px] items-center text-gray-700 hover:underline" to="/como-usar">Como usar</Link></li><li><Link className="inline-flex min-h-[44px] items-center text-gray-700 hover:underline" to="/sobre">Sobre</Link></li><li><Link className="inline-flex min-h-[44px] items-center text-gray-700 hover:underline" to="/solicitar-acesso">Solicitar acesso</Link></li></ul></div>
          <div><h2 className="font-semibold text-tinta">Privacidade</h2><ul className="mt-1"><li><Link className="inline-flex min-h-[44px] items-center text-gray-700 hover:underline" to="/privacidade">Política de privacidade</Link></li><li><Link className="inline-flex min-h-[44px] items-center text-gray-700 hover:underline" to="/excluir-conta">Excluir a conta</Link></li></ul></div>
          <div><h2 className="font-semibold text-tinta">Contato e lojas</h2><ul className="mt-1"><li className="flex min-h-[44px] items-center break-all text-gray-700">contato@portalsimulados.com.br</li><li><a className="inline-flex min-h-[44px] items-center text-gray-700 hover:underline" href={lojas.appStore} rel="noopener">App Store</a></li>{lojas.googlePlay && <li><a className="inline-flex min-h-[44px] items-center text-gray-700 hover:underline" href={lojas.googlePlay} rel="noopener">Google Play</a></li>}<li><Link className="inline-flex min-h-[44px] items-center text-gray-700 hover:underline" to="/como-usar#aplicativo-para-computador">Windows e Mac</Link></li></ul></div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
